import { Fragment, type ReactNode } from "react";
import { ArrowDown, ArrowUp, Copy, LayoutTemplate, Plus, Trash2, Type } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { EditorDeTextoRico } from "@/components/finance/editor/EditorDeTextoRico";
import type {
  CatalogoDoModelo,
  ModeloDeFaturamento,
  SecaoBloco,
  SecaoDoModelo,
  SecaoTexto,
  TemplateTipo,
} from "@/types/finance";
import { ehModeloDeInvite } from "@/types/finance";

const SEMPRE = "sempre";
const SEM_CHAVE = "nenhuma";

function novoId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `secao-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function novaSecaoDeTexto(): SecaoTexto {
  return { id: novoId(), tipo: "texto", titulo: null, html: "<p></p>", chave: null, mostrar_quando: null };
}

interface EditorDeSecoesProps {
  tipo: TemplateTipo;
  secoes: SecaoDoModelo[];
  onChange: (secoes: SecaoDoModelo[]) => void;
  catalogo: CatalogoDoModelo | undefined;
  disabled?: boolean;
}

/** O modelo como uma folha: textos editáveis direto e blocos gerados pelo sistema, na ordem do documento. */
export function EditorDeSecoes({ tipo, secoes, onChange, catalogo, disabled }: EditorDeSecoesProps) {
  const blocosDisponiveis = (catalogo?.blocos ?? []).filter(
    (bloco) => !secoes.some((secao) => secao.tipo === "bloco" && secao.bloco === bloco.bloco),
  );

  const atualizar = (indice: number, secao: SecaoDoModelo) =>
    onChange(secoes.map((atual, posicao) => (posicao === indice ? secao : atual)));

  const inserir = (indice: number, secao: SecaoDoModelo) =>
    onChange([...secoes.slice(0, indice), secao, ...secoes.slice(indice)]);

  const mover = (indice: number, deslocamento: -1 | 1) => {
    const destino = indice + deslocamento;
    if (destino < 0 || destino >= secoes.length) return;
    const reordenadas = [...secoes];
    [reordenadas[indice], reordenadas[destino]] = [reordenadas[destino], reordenadas[indice]];
    onChange(reordenadas);
  };

  const remover = (indice: number) => onChange(secoes.filter((_, posicao) => posicao !== indice));

  const barraDeInsercao = (indice: number, sempreVisivel = false) =>
    disabled ? null : (
      <BarraDeInsercao
        sempreVisivel={sempreVisivel}
        blocosDisponiveis={blocosDisponiveis}
        onAdicionarTexto={() => inserir(indice, novaSecaoDeTexto())}
        onAdicionarBloco={(bloco) => inserir(indice, { id: novoId(), tipo: "bloco", bloco })}
      />
    );

  return (
    <div className="mx-auto w-full max-w-[794px] rounded-sm bg-white px-6 py-8 shadow-md ring-1 ring-black/5 sm:px-12">
      {secoes.length === 0 && (
        <p className="py-10 text-center text-sm text-muted-foreground">O documento está vazio.</p>
      )}
      {secoes.map((secao, indice) => (
        <Fragment key={secao.id}>
          {barraDeInsercao(indice)}
          <MolduraDaSecao
            disabled={disabled}
            primeira={indice === 0}
            ultima={indice === secoes.length - 1}
            onSubir={() => mover(indice, -1)}
            onDescer={() => mover(indice, 1)}
            onRemover={() => remover(indice)}
            onDuplicar={
              secao.tipo === "texto" ? () => inserir(indice + 1, { ...secao, id: novoId() }) : undefined
            }
          >
            {secao.tipo === "texto" ? (
              <SecaoDeTexto
                tipo={tipo}
                secao={secao}
                catalogo={catalogo}
                disabled={disabled}
                onChange={(atualizada) => atualizar(indice, atualizada)}
              />
            ) : (
              <SecaoDeBloco secao={secao} catalogo={catalogo} />
            )}
          </MolduraDaSecao>
        </Fragment>
      ))}
      {barraDeInsercao(secoes.length, true)}
    </div>
  );
}

interface MolduraDaSecaoProps {
  children: ReactNode;
  disabled?: boolean;
  primeira: boolean;
  ultima: boolean;
  onSubir: () => void;
  onDescer: () => void;
  onRemover: () => void;
  onDuplicar?: () => void;
}

function MolduraDaSecao({
  children,
  disabled,
  primeira,
  ultima,
  onSubir,
  onDescer,
  onRemover,
  onDuplicar,
}: MolduraDaSecaoProps) {
  return (
    <div className="group/secao relative -mx-3 rounded-md px-3 py-1 transition-colors hover:bg-slate-50/80 focus-within:bg-slate-50/60">
      {!disabled && (
        <div className="absolute -right-2 top-1 z-20 flex translate-x-full flex-col gap-0.5 opacity-0 transition-opacity group-hover/secao:opacity-100 group-focus-within/secao:opacity-100 max-sm:static max-sm:mb-1 max-sm:translate-x-0 max-sm:flex-row max-sm:justify-end max-sm:opacity-100">
          <BotaoDaMoldura rotulo="Subir" onClick={onSubir} disabled={primeira}>
            <ArrowUp className="h-3.5 w-3.5" />
          </BotaoDaMoldura>
          <BotaoDaMoldura rotulo="Descer" onClick={onDescer} disabled={ultima}>
            <ArrowDown className="h-3.5 w-3.5" />
          </BotaoDaMoldura>
          {onDuplicar && (
            <BotaoDaMoldura rotulo="Duplicar" onClick={onDuplicar}>
              <Copy className="h-3.5 w-3.5" />
            </BotaoDaMoldura>
          )}
          <BotaoDaMoldura rotulo="Tirar do documento" onClick={onRemover} perigo>
            <Trash2 className="h-3.5 w-3.5" />
          </BotaoDaMoldura>
        </div>
      )}
      {children}
    </div>
  );
}

function BotaoDaMoldura({
  children,
  rotulo,
  onClick,
  disabled,
  perigo,
}: {
  children: ReactNode;
  rotulo: string;
  onClick: () => void;
  disabled?: boolean;
  perigo?: boolean;
}) {
  return (
    <Button
      type="button"
      size="icon"
      variant="outline"
      aria-label={rotulo}
      title={rotulo}
      disabled={disabled}
      onClick={onClick}
      className={cn("h-7 w-7 bg-background shadow-xs", perigo && "hover:border-destructive/40 hover:text-destructive")}
    >
      {children}
    </Button>
  );
}

interface SecaoDeTextoProps {
  tipo: TemplateTipo;
  secao: SecaoTexto;
  catalogo: CatalogoDoModelo | undefined;
  disabled?: boolean;
  onChange: (secao: SecaoTexto) => void;
}

function SecaoDeTexto({ tipo, secao, catalogo, disabled, onChange }: SecaoDeTextoProps) {
  const modelos = catalogo?.modelos ?? [];
  const chaves = catalogo?.chaves ?? [];
  const rotuloDoModelo = modelos.find((modelo) => modelo.id === secao.mostrar_quando)?.rotulo;
  const rotuloDaChave = chaves.find((chave) => chave.chave === secao.chave)?.rotulo;

  return (
    <div className="space-y-1 py-1">
      <input
        type="text"
        value={secao.titulo ?? ""}
        disabled={disabled}
        onChange={(evento) => onChange({ ...secao, titulo: evento.target.value || null })}
        placeholder="Título da seção (opcional)"
        aria-label="Título da seção"
        className="w-full bg-transparent text-sm font-bold text-foreground placeholder:font-normal placeholder:text-muted-foreground/60 focus:outline-hidden"
      />
      <EditorDeTextoRico
        aparencia="folha"
        ariaLabel={secao.titulo ? `Texto: ${secao.titulo}` : "Texto da seção"}
        value={secao.html}
        disabled={disabled}
        variaveis={catalogo?.variaveis}
        onChange={(html) => onChange({ ...secao, html })}
      />
      <div className="flex flex-wrap items-center gap-1.5 pt-1">
        {rotuloDoModelo && (
          <Badge variant="outline" className="border-violet-200 bg-violet-50 text-[10px] text-violet-700">
            {rotuloDoModelo}
          </Badge>
        )}
        {rotuloDaChave && (
          <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-[10px] text-emerald-700">
            Editável em cada proposta: {rotuloDaChave}
          </Badge>
        )}
        {!disabled && (
          <div className="hidden flex-wrap items-center gap-1.5 group-hover/secao:flex group-focus-within/secao:flex">
            <Select
              value={secao.mostrar_quando ?? SEMPRE}
              onValueChange={(valor) =>
                onChange({ ...secao, mostrar_quando: valor === SEMPRE ? null : (valor as ModeloDeFaturamento) })
              }
            >
              <SelectTrigger className="h-7 w-auto gap-1 border-dashed px-2 text-[11px] text-muted-foreground">
                <span className="text-muted-foreground/80">Aparece em:</span>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={SEMPRE}>Todos os documentos</SelectItem>
                {modelos.map((modelo) => (
                  <SelectItem key={modelo.id} value={modelo.id}>
                    {modelo.rotulo}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!ehModeloDeInvite(tipo) && chaves.length > 0 && (
              <Select
                value={secao.chave ?? SEM_CHAVE}
                onValueChange={(valor) => onChange({ ...secao, chave: valor === SEM_CHAVE ? null : valor })}
              >
                <SelectTrigger className="h-7 w-auto gap-1 border-dashed px-2 text-[11px] text-muted-foreground">
                  <span className="text-muted-foreground/80">Editável na proposta:</span>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={SEM_CHAVE}>Não</SelectItem>
                  {chaves.map((chave) => (
                    <SelectItem key={chave.chave} value={chave.chave}>
                      {chave.rotulo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SecaoDeBloco({ secao, catalogo }: { secao: SecaoBloco; catalogo: CatalogoDoModelo | undefined }) {
  const bloco = catalogo?.blocos.find((item) => item.bloco === secao.bloco);
  return (
    <div className="my-1 flex items-start gap-3 rounded-md border border-dashed border-slate-300 bg-slate-100/80 px-4 py-3">
      <LayoutTemplate className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
      <div className="min-w-0">
        <p className="text-sm font-medium text-slate-700">{bloco?.rotulo ?? "Parte automática"}</p>
        <p className="text-xs text-slate-500">
          {bloco?.descricao ?? "Preenchida pelo sistema com os dados de cada documento."}
        </p>
      </div>
    </div>
  );
}

interface BarraDeInsercaoProps {
  sempreVisivel: boolean;
  blocosDisponiveis: CatalogoDoModelo["blocos"];
  onAdicionarTexto: () => void;
  onAdicionarBloco: (bloco: string) => void;
}

function BarraDeInsercao({ sempreVisivel, blocosDisponiveis, onAdicionarTexto, onAdicionarBloco }: BarraDeInsercaoProps) {
  return (
    <div
      className={cn(
        "group/insercao relative flex h-6 items-center justify-center",
        sempreVisivel && "mt-4 h-auto",
      )}
    >
      <div
        className={cn(
          "absolute inset-x-0 top-1/2 h-px bg-primary/30 opacity-0 transition-opacity group-hover/insercao:opacity-100",
          sempreVisivel && "hidden",
        )}
      />
      <div
        className={cn(
          "relative flex gap-1 opacity-0 transition-opacity group-hover/insercao:opacity-100 focus-within:opacity-100",
          sempreVisivel && "opacity-100",
        )}
      >
        <Button type="button" size="sm" variant="outline" className="h-6 gap-1 bg-background px-2 text-[11px]" onClick={onAdicionarTexto}>
          <Type className="h-3 w-3" />
          Adicionar texto
        </Button>
        {blocosDisponiveis.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" size="sm" variant="outline" className="h-6 gap-1 bg-background px-2 text-[11px]">
                <Plus className="h-3 w-3" />
                Adicionar parte automática
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" className="w-72">
              <DropdownMenuLabel className="text-xs text-muted-foreground">Partes preenchidas pelo sistema</DropdownMenuLabel>
              {blocosDisponiveis.map((bloco) => (
                <DropdownMenuItem
                  key={bloco.bloco}
                  onSelect={() => onAdicionarBloco(bloco.bloco)}
                  className="flex flex-col items-start gap-0"
                >
                  <span className="text-sm">{bloco.rotulo}</span>
                  {bloco.descricao && <span className="text-[11px] text-muted-foreground">{bloco.descricao}</span>}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}
